// biome-ignore-all lint/a11y: TODO - address a11y issues later
import { PersonIcon } from "@radix-ui/react-icons";
import type React from "react";
import type { Person } from "@/hooks/api/use-account-persons";
import { cn } from "@/lib/utils";

export interface PersonScatterPoint {
  timestamp: number;
  people: Person[];
  displayInitials: string;
  y: number;
  // Optional payload fields that might be passed by recharts
  id?: string;
  cx?: number;
  cy?: number;
  x?: number;
}

// Define the type for the mouse enter payload explicitly
// Note: Recharts payload type can be complex, adjust if needed
export type PersonAvatarPayload = PersonScatterPoint | undefined;

/**
 * A custom scatter shape component for rendering person avatars on charts
 * Used primarily for first touchpoints visualization
 */
export default function PersonAvatar({
  cx,
  cy,
  payload,
  onClick,
  containerHeight,
  // Add event handlers to props
  onMouseEnter,
  onMouseLeave,
}: {
  cx?: number;
  cy?: number;
  payload?: PersonScatterPoint;
  onClick?: (personId: string, timestamp: string) => void;
  containerHeight?: number;
  // Define prop types for handlers
  onMouseEnter?: (
    payload: PersonAvatarPayload,
    event: React.MouseEvent,
  ) => void;
  onMouseLeave?: () => void;
}): JSX.Element | null {
  if (!cx || !cy || !payload) return null;

  const hasInitials =
    payload.displayInitials && payload.displayInitials !== "+1";

  // Calculate size based on container height (only apply scaling when < 100px)
  const isCompact = containerHeight && containerHeight < 100;

  // Calculate scaling factor - from 1.0 at 100px down to 0.6 at 50px or less
  const scaleFactor = isCompact
    ? Math.max(0.6, 0.6 + ((containerHeight - 50) / 50) * 0.4)
    : 1.0;

  // Avatar dimensions with scaling
  const normalRadius = 12;
  const hoverRadius = 14;
  const radius = normalRadius * scaleFactor;
  const hoverRadiusScaled = hoverRadius * scaleFactor;

  // Font sizes with scaling
  const normalFontSize = 10;
  const hoverFontSize = 11;
  const fontSize = normalFontSize * scaleFactor;
  const hoverFontSize2 = hoverFontSize * scaleFactor;

  // Icon positioning with scaling
  const iconOffsetX = 7.5 * scaleFactor;
  const iconOffsetY = 8 * scaleFactor;
  const iconHoverOffsetX = 9 * scaleFactor;
  const iconHoverOffsetY = 9.5 * scaleFactor;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent triggering chart-level click
    if (payload.people.length > 0) {
      const personIds = payload.people.map((p) => p.id).join(",");
      const timestamp = new Date(payload.timestamp).toISOString();
      onClick?.(personIds, timestamp);
    }
  };

  const handleMouseEnter = (e: React.MouseEvent) => {
    onMouseEnter?.(payload, e); // Pass payload and event
  };

  const handleMouseLeave = () => {
    onMouseLeave?.();
  };

  return (
    // Attach handlers to the outer group element
    <g
      style={{ pointerEvents: "all" }} // Ensure mouse events are captured
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      className="person-avatar"
    >
      <style>
        {`
          .person-avatar circle {
            transition: r 0.15s ease;
            r: ${radius};
          }
          .person-avatar:hover circle {
            r: ${hoverRadiusScaled};
          }
          .person-avatar .person-icon {
            transition: all 0.15s ease;
            transform: translate(${cx - iconOffsetX}px, ${cy - iconOffsetY}px);
          }
          .person-avatar:hover .person-icon {
            transform: translate(${cx - iconHoverOffsetX}px, ${
              cy - iconHoverOffsetY
            }px);
          }
          .person-avatar text {
            transition: font-size 0.15s ease;
            font-size: ${fontSize}px;
          }
          .person-avatar:hover text {
            font-size: ${hoverFontSize2}px;
          }
        `}
      </style>
      <circle
        cx={cx}
        cy={cy}
        className="fill-muted-foreground dark:fill-muted cursor-pointer"
        style={{ pointerEvents: "all" }}
      />
      {hasInitials ? (
        <text
          x={cx}
          y={cy}
          dy=".3em"
          textAnchor="middle"
          className="fill-background dark:fill-foreground cursor-pointer"
          fontWeight="bold"
          style={{ pointerEvents: "none" }} // Text shouldn't block hover on circle
        >
          {payload.displayInitials}
        </text>
      ) : (
        <g className="person-icon">
          <PersonIcon
            className={cn(
              "text-background dark:text-foreground",
              isCompact ? "h-3 w-3" : "h-4 w-4",
            )}
          />
        </g>
      )}
    </g>
  );
}
